/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateUpdater, useRef, useState } from 'preact/hooks';
import Usercentrics from '@usercentrics/cmp-browser-sdk';
import { isEqual } from '../modules/helpers';
import { v4 as uuidv4 } from 'uuid';

type Variables = any;
type Data = { [key: string]: any } | null;
type Method = (...args: Variables) => Promise<FetchResult | void>;
type WrapperMethod = (...args: Variables) => Promise<FetchResult>;
type MutationResultUpdater = StateUpdater<MutationResult>;
type MutationTuple = [WrapperMethod, MutationResult];

interface FetchResult {
  data?: Data;
  error?: Error;
}

interface MutationResult {
  error?: Error | null;
  called: boolean;
  data?: Data | null;
  loading: boolean;
}

class MutationData {
  public scope: Usercentrics;
  public isMounted = false;
  private method: Method;
  private mostRecentMutationId: string;
  private previousResult?: MutationResult;
  private result: MutationResult;
  private setResult: MutationResultUpdater;

  constructor({
    scope,
    method,
    result,
    setResult,
  }: {
    scope: Usercentrics;
    method: Method;
    result: MutationResult;
    setResult: MutationResultUpdater;
  }) {
    this.scope = scope;
    this.method = method;
    this.mostRecentMutationId = uuidv4();
    this.result = result;
    this.setResult = setResult;
    this.runMutation = this.runMutation.bind(this);
  }

  public execute(result: MutationResult): MutationTuple {
    this.isMounted = true;

    return [this.runMutation, result];
  }

  private async runMutation(...vars: Variables): Promise<FetchResult> {
    this.onMutationStart();
    const mutationId = this.generateNewMutationId();

    return this.mutate(...vars)
      .then((response: FetchResult | void) => {
        this.onMutationCompleted(response, mutationId);

        return response || { data: null };
      })
      .catch((error: Error) => {
        this.onMutationError(error, mutationId);

        throw error;
      });
  }

  private mutate(...vars: Variables): Promise<FetchResult | void> {
    return this.method.call(this.scope, ...vars);
  }

  private onMutationStart() {
    if (!this.result.loading) {
      this.updateResult({
        called: true,
        data: null,
        error: null,
        loading: true,
      });
    }
  }

  private onMutationCompleted(response: FetchResult | void, mutationId: string) {
    if (this.isMostRecentMutation(mutationId)) {
      this.updateResult({
        called: true,
        data: response ? response.data : null,
        error: null,
        loading: false,
      });
    }
  }

  private onMutationError(error: Error, mutationId: string) {
    if (this.isMostRecentMutation(mutationId)) {
      this.updateResult({
        called: true,
        data: null,
        error,
        loading: false,
      });
    }
  }

  private generateNewMutationId(): string {
    const newMutationId = uuidv4();
    this.mostRecentMutationId = newMutationId;

    return newMutationId;
  }

  private isMostRecentMutation(mutationId: string) {
    return this.mostRecentMutationId === mutationId;
  }

  private updateResult(result: MutationResult) {
    if (this.isMounted && (!this.previousResult || !isEqual(this.previousResult, result))) {
      this.setResult(result);
      this.previousResult = result;
    }
  }
}

// This hook wraps the sdk call and return the call together with state variables
// such as loading and called
export const useMutation = (method: Method, scope: Usercentrics): MutationTuple => {
  const [result, setResult] = useState<MutationResult>({ called: false, loading: false });
  const mutationDataRef = useRef<MutationData>();

  const getMutationDataRef = () => {
    if (!mutationDataRef.current) {
      mutationDataRef.current = new MutationData({
        method,
        result,
        scope,
        setResult,
      });
    }

    return mutationDataRef.current;
  };

  const mutationData = getMutationDataRef();

  return mutationData.execute(result);
};
